import React, { useEffect, useState } from "react";
import * as Pi from "@phosphor-icons/react";
import { PageLoader, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { money, apiErr } from "../../lib/api";

const MODELS = ["free", "fixed", "commission", "per_ticket", "per_attendee", "per_event", "per_day", "subscription", "starting", "quantity", "package", "quotation"];
const CATS = ["Ticketing", "Marketing", "Operations", "Additional"];

const empty = { name: "", category: "Marketing", pricing_model: "fixed", price: 0, gst_percent: 18, short_description: "", description: "", icon: "Sparkle", featured: false, active: true };

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/services").then((r) => setServices(r.data.services)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit({ ...empty }); setOpen(true); };
  const openEdit = (s) => { setEdit({ ...s }); setOpen(true); };

  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/services/${edit.id}`, edit);
      else await api.post("/admin/services", edit);
      toast.success("Saved. Public pages reflect changes instantly."); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <PageLoader />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><div className="overline mb-1">Marketplace</div><h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Services & Pricing</h1></div>
        <Button onClick={openNew} className="rounded-full" data-testid="add-service"><Plus className="mr-1.5" /> Add service</Button>
      </div>

      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {services.map((s) => {
          const Icon = Pi[s.icon] || Pi.Sparkle;
          return (
            <div key={s.id} className="flex items-center gap-3 p-4" data-testid={`admin-service-${s.id}`}>
              <span className="w-10 h-10 grid place-items-center rounded-lg bg-primary/10 text-primary shrink-0"><Icon size={18} weight="fill" /></span>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{s.name}</div>
                <div className="text-sm text-muted-foreground">{s.category} · {s.pricing_model} · {money(s.price)} · {s.gst_percent}% GST</div>
              </div>
              <StatusBadge status={s.active ? "active" : "unpublished"} />
              <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><PencilSimple size={14} className="mr-1" /> Edit</Button>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit" : "New"} service</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="mt-1.5" data-testid="svc-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label>
                  <Select value={edit.category} onValueChange={(v) => setEdit({ ...edit, category: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label>Pricing model</Label>
                  <Select value={edit.pricing_model} onValueChange={(v) => setEdit({ ...edit, pricing_model: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label>Price (₹)</Label><Input type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: +e.target.value })} className="mt-1.5" data-testid="svc-price" /></div>
                <div><Label>GST %</Label><Input type="number" value={edit.gst_percent} onChange={(e) => setEdit({ ...edit, gst_percent: +e.target.value })} className="mt-1.5" /></div>
              </div>
              <div><Label>Short description</Label><Input value={edit.short_description} onChange={(e) => setEdit({ ...edit, short_description: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Description</Label><Textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="mt-1.5" rows={2} /></div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><Switch checked={edit.featured} onCheckedChange={(v) => setEdit({ ...edit, featured: v })} /> Featured</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} /> Active</label>
              </div>
              <DialogFooter><Button onClick={save} className="w-full rounded-full" data-testid="save-service">Save</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
